/**
 * Autorização da baixa da TARP (GUPM-184). TS puro.
 * -------------------------------------------------
 * Prod exige TOTP (RFC 6238), verificado na borda via Edge `verify-totp`.
 * Em DEV, o Edge não sobe (503 local); um PIN de teste destrava o fluxo E2E,
 * registrando a baixa como NÃO-verificada (`baixa_totp_verified=false`). A RLS
 * RT-only continua sendo o gate real. Só a decisão do bypass é pura; a
 * verificação TOTP em si é I/O (datasource/hook).
 */
import type { TotpErrorCode } from '../totp';

/** PIN de teste aceito apenas em ambiente DEV. */
export const DEV_AUTH_PIN = '1234';

/** Em DEV, o PIN de teste destrava a baixa. Em prod, sempre falso (exige TOTP). */
export function isDevAuthBypass(code: string, isDev: boolean): boolean {
  return isDev && code.trim() === DEV_AUTH_PIN;
}

/**
 * Mensagens da baixa por código de erro do `verify-totp` (contexto: RT
 * autorizando o encerramento). Preserva a informação (bloqueio, sem cadastro,
 * serviço fora) em vez de colapsar tudo em "código inválido".
 *
 * É `Record<TotpErrorCode, string>` EXAUSTIVO — não um `switch` com `default`:
 * um código novo no contrato do `verify-totp` quebra o build aqui, forçando uma
 * mensagem consciente para a baixa (dedup do review do PR #99). Os textos são
 * próprios deste contexto; a troca de plantão tem os seus em `shift-handover/hooks.ts`.
 */
const BAIXA_AUTH_MESSAGES: Record<TotpErrorCode, string> = {
  invalid_code: 'Código de autenticação inválido.',
  locked_out: 'Muitas tentativas: autenticador bloqueado por alguns minutos. Aguarde e tente de novo.',
  no_totp_set: 'Você ainda não cadastrou um autenticador. Cadastre em Perfil → Autenticador.',
  unauthorized: 'Sessão inválida para autorizar. Faça login novamente.',
  not_found: 'Operador não encontrado para autorizar.',
  enrollment_self_only: 'Código de autenticação inválido.',
  service_unavailable: 'Serviço de verificação indisponível no momento. Tente novamente em instantes.',
};

/**
 * Mensagem acionável para a falha de autorização da baixa. O aviso do PIN de
 * teste só aparece em DEV — em produção NÃO se revela o bypass (GUPM-184
 * self-review). Código ausente/desconhecido cai em `invalid_code`.
 */
export function baixaAuthErrorMessage(errorCode: string | undefined, isDev: boolean): string {
  const base =
    errorCode && errorCode in BAIXA_AUTH_MESSAGES
      ? BAIXA_AUTH_MESSAGES[errorCode as TotpErrorCode]
      : BAIXA_AUTH_MESSAGES.invalid_code;
  return isDev ? `${base} (ambiente de teste: use o PIN ${DEV_AUTH_PIN})` : base;
}
